/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { tl } from '../../lib/testingLabApi';
import { cn } from '../../lib/utils';

const getRecognizer = () => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  return SR ? new SR() : null;
};

/**
 * Live mic transcription. Uses Web Speech API for instant transcription;
 * falls back to MediaRecorder + Whisper backend if SpeechRecognition is unavailable.
 *
 * On stop, calls onTranscript(finalText) and (if sessionId provided) attaches a
 * voice_note evidence with transcript pre-filled.
 */
export const LiveTranscriptionButton = ({
  sessionId,
  stepId,
  onTranscript,
  appendMode = true,
  size = 'sm',
  label = 'Talk',
}) => {
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState('');
  const [finalText, setFinalText] = useState('');
  const [busy, setBusy] = useState(false);
  const recognizerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fallbackMode = useRef(false);

  const cleanup = () => {
    try { recognizerRef.current?.stop(); } catch { /* */ }
    try { mediaRecorderRef.current?.stop(); } catch { /* */ }
    recognizerRef.current = null;
    mediaRecorderRef.current = null;
  };

  useEffect(() => () => cleanup(), []);

  const startWebSpeech = () => {
    const r = getRecognizer();
    if (!r) return false;
    r.continuous = true;
    r.interimResults = true;
    r.lang = navigator.language || 'en-US';
    let accumFinal = '';
    r.onresult = (event) => {
      let interimT = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const txt = event.results[i][0].transcript;
        if (event.results[i].isFinal) accumFinal += txt + ' ';
        else interimT += txt;
      }
      setInterim(interimT);
      if (accumFinal) setFinalText((prev) => (prev + ' ' + accumFinal).trim());
    };
    r.onerror = (e) => {
      if (e.error === 'no-speech' || e.error === 'aborted') return;
      toast.error(`Mic: ${e.error}`);
      setRecording(false);
    };
    r.onend = () => {
      // Auto-restart if user is still in recording state (Chrome stops after silence)
      if (recognizerRef.current === r && recording) {
        try { r.start(); } catch { /* */ }
      }
    };
    try { r.start(); } catch (e) { return false; }
    recognizerRef.current = r;
    return true;
  };

  const startMediaRecorder = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (!sessionId || chunksRef.current.length === 0) return;
        setBusy(true);
        try {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
          const ev = await tl.uploadEvidence(sessionId, file, {
            evidence_type: 'voice_note',
            step_id: stepId || null,
            title: 'Voice note (live)',
          });
          if (ev.transcript) {
            setFinalText(ev.transcript);
            onTranscript && onTranscript(ev.transcript, { evidence_id: ev.evidence_id });
            toast.success('Whisper transcribed');
          } else {
            toast.success('Voice note saved');
          }
        } catch (e) { toast.error(e.message); }
        finally { setBusy(false); }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      return true;
    } catch (e) {
      toast.error(`Mic permission: ${e.message}`);
      return false;
    }
  };

  const start = async () => {
    setInterim(''); setFinalText('');
    setRecording(true);
    if (startWebSpeech()) {
      // Web Speech is fine — not using MediaRecorder
      fallbackMode.current = false;
      // ALSO start MediaRecorder in background to attach the audio file as evidence
      // (only if sessionId is provided)
      if (sessionId) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          const mr = new MediaRecorder(stream);
          chunksRef.current = [];
          mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
          mr.onstop = async () => {
            stream.getTracks().forEach((t) => t.stop());
            if (chunksRef.current.length === 0) return;
            try {
              const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
              const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
              await tl.uploadEvidence(sessionId, file, {
                evidence_type: 'voice_note',
                step_id: stepId || null,
                title: 'Voice note (live)',
                note: finalText || null,
              });
            } catch { /* non-fatal */ }
          };
          mr.start();
          mediaRecorderRef.current = mr;
        } catch { /* mic capture optional — speech rec still works */ }
      }
      return;
    }
    // Fallback: MediaRecorder + Whisper backend
    fallbackMode.current = true;
    toast.info('Using Whisper transcription (Web Speech unavailable)');
    const ok = await startMediaRecorder();
    if (!ok) setRecording(false);
  };

  const stop = () => {
    setRecording(false);
    cleanup();
    // Push final transcript to caller
    if (finalText && onTranscript && !fallbackMode.current) {
      onTranscript(finalText, {});
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        size={size}
        variant={recording ? 'destructive' : 'outline'}
        onClick={recording ? stop : start}
        disabled={busy}
        className={cn('h-7 text-xs', recording && 'animate-pulse')}
        data-testid="live-transcription-btn"
      >
        {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> :
         recording ? <MicOff className="w-3 h-3 mr-1" /> : <Mic className="w-3 h-3 mr-1" />}
        {recording ? 'Stop' : label}
      </Button>
      {recording && (
        <Badge className="bg-red-500 text-white text-[10px] h-5 animate-pulse" data-testid="live-transcription-badge">
          ● REC
        </Badge>
      )}
      {(interim || finalText) && (
        <span className="text-xs text-muted-foreground italic max-w-[280px] truncate" data-testid="live-transcription-preview">
          {finalText && <span className="text-foreground not-italic">{finalText.slice(-80)}</span>}
          {interim && <span> {interim.slice(-60)}</span>}
        </span>
      )}
    </div>
  );
};

export default LiveTranscriptionButton;
